<template>
    <div
        ref="container"
        :class="{
            [props.emptyClass || '']: props.emptyClass && !isBannerVisible && !slotConfig.placeholderHeight,
            [props.backgroundClass || '']: props.backgroundClass && (!adDisabledStore.isAdDisabled || showAdFreeBanner),
        }"
        :style="{
            '--slot-max-height': slotHeight ? `${slotHeight}px` : 'auto',
        }"
    >
        <div
            :class="$style.wrapper"
            :style="{
                '--placeholder-height': slotConfig.placeholderHeight ? `${slotConfig.placeholderHeight}px` : 'auto',
            }"
        >
            <AdFreeBanner
                v-if="showAdFreeBanner"
                :type="type"
            />
            <div
                v-else
                :id="id"
                :class="$style.container"
            />
            <slot :isVisible="isBannerVisible" />
        </div>
    </div>
</template>
<script lang="ts" setup>
import { useIntersectionObserver, useResizeObserver, useThrottleFn } from '@vueuse/core'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useId, useTemplateRef, watch } from 'vue'

import AdFreeBanner from '@/components_new/AdFreeBanner/AdFreeBanner.vue'
import { EXISTING_AD_FREE_BANNERS } from '@/components_new/AdFreeBanner/banners'
import type { PublicPageAdBanner } from '@/modules/adv/public-page-ad-banner'
import type { PageAdSize, PageAdType } from '@/modules/adv/types'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'
import { idleCallbackPromise } from '@/utils/helpers'

const adsStore = useAdsStore()
const adDisabledStore = useAdDisabledStore()

const SlotsConfig: Record<PageAdType, { sizes: PageAdSize[]; placeholderHeight?: number }> = {
    sidebar: {
        sizes: [[300, 300], 'fluid'],
        placeholderHeight: 300,
    },
    sidebar_bottom: {
        sizes: [[300, 300], [300, 600], [120, 600], [160, 600], [300, 250], 'fluid'],
    },
    sidebar_fullscreen: {
        sizes: [[300, 600], [160, 600], [120, 600], 'fluid'],
        placeholderHeight: 600,
    },
    sidebar_bottom_fullscreen: {
        sizes: [[300, 300], [300, 250], 'fluid'],
    },
    leaderboard: {
        sizes: [[728, 90], [950, 90], [960, 90], [970, 90], [980, 90], 'fluid'],
        placeholderHeight: 90,
    },
    leaderboard_rest: {
        sizes: [[728, 90], [950, 90], [960, 90], [970, 90], [980, 90], 'fluid'],
        placeholderHeight: 90,
    },
    sticky_mobile_non_game_page: {
        sizes: [[320, 100], [300, 50], [320, 50], 'fluid'],
    },
    sticky_mobile: {
        sizes: [[300, 50], [320, 50], 'fluid'],
    },
    sticky_tablet: {
        sizes: [[728, 90], 'fluid'],
    },
    sticky_desktop: {
        sizes: [[970, 90], [960, 90], [950, 90], [980, 90], [728, 90], 'fluid'],
    },

    widget_sidebar: {
        sizes: [[120, 600], [300, 300], [300, 600], [160, 600], [300, 250], 'fluid'],
    },
    widget_sidebar_bottom: {
        sizes: [[300, 250], 'fluid'],
    },
    widget_horizontal: {
        sizes: [[728, 90], 'fluid'],
    },
    widget_horizontal_2: {
        sizes: [[728, 90], 'fluid'],
    },
    widget_sticky_mobile: {
        sizes: [[300, 50], [320, 50], 'fluid'],
    },
    widget_sticky_tablet: {
        sizes: [[728, 90], 'fluid'],
    },
    universal_banner_1: {
        sizes: [
            [960, 90],
            [300, 600],
            [970, 90],
            [300, 250],
            [336, 280],
            [320, 100],
            [950, 90],
            [320, 480],
            [300, 50],
            [480, 320],
            [300, 300],
            [728, 90],
            [120, 600],
            [320, 50],
            [160, 600],
            [300, 100],
            [980, 90],
        ],
    },
    universal_banner_short_1: {
        sizes: [
            [300, 250],
            [320, 100],
            [320, 50],
            [300, 100],
            [300, 50],
            [300, 300],
            [120, 600],
            [160, 600],
        ],
    },
    universal_banner_2: {
        sizes: [
            [960, 90],
            [300, 600],
            [970, 90],
            [300, 250],
            [336, 280],
            [320, 100],
            [950, 90],
            [320, 480],
            [300, 50],
            [480, 320],
            [300, 300],
            [728, 90],
            [120, 600],
            [320, 50],
            [160, 600],
            [300, 100],
            [980, 90],
        ],
    },
    // partner ads sdk formats, here just for type safety
    vertical_1: {
        sizes: [],
    },
    vertical_2: {
        sizes: [],
    },
    vertical_3: {
        sizes: [],
    },
    horizontal_1: {
        sizes: [],
    },
}

type Props = {
    type: PageAdType
    /**
     * Class to add when the ad is not loaded
     * Should not hide element with "display: none", otherwise the ad will never be loaded
     */
    emptyClass?: string
    backgroundClass?: string
    intersectionRootMargin?: string
    refreshSec?: number
    respectHeight?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
}>()
const slotConfig = computed(() => SlotsConfig[props.type])

const showAdFreeBanner = computed(
    () => adDisabledStore.isAdFreeActive && EXISTING_AD_FREE_BANNERS[props.type] !== undefined,
)

const slotHeight = computed(() => {
    const heights = slotConfig.value.sizes.filter((s) => s !== 'fluid').map(([, h]) => h)
    return heights.length ? Math.max(...heights) : undefined
})

// get sorted sizes in descending width order, "fluid" is always the last — it can be any size
const sortedSizesDesc = computed(() =>
    slotConfig.value.sizes.slice().sort((a, b) => {
        if (a === 'fluid') {
            return 1
        }
        if (b === 'fluid') {
            return -1
        }
        return b[0] - a[0]
    }),
)

const banner = shallowRef<PublicPageAdBanner>()
const isBannerVisible = ref(false)

const id = useId()
const containerRef = useTemplateRef('container')
const lastFilteredSizes = ref<PageAdSize[]>()
const readyForAdRequest = ref(props.intersectionRootMargin === undefined)

async function updateBanner() {
    await idleCallbackPromise()

    if (!readyForAdRequest.value || !adsStore.adService || !containerRef.value || adDisabledStore.isAdDisabled) {
        return
    }

    const rect = containerRef.value.getBoundingClientRect()
    const filteredSizes = sortedSizesDesc.value.filter((size) => {
        if (size === 'fluid') {
            return rect.width > 0
        }
        const heightFits = props.respectHeight ? rect.height >= size[1] : true
        return rect.width >= size[0] && heightFits
    })
    // do nothing, if the max size is the same
    if (JSON.stringify(filteredSizes) === JSON.stringify(lastFilteredSizes.value)) {
        return
    }

    lastFilteredSizes.value = filteredSizes

    // destroy the previous banner
    banner.value?.destroy()
    banner.value = undefined

    // prepare a new banner if there are sizes
    if (filteredSizes.length > 0 && rect.width > 0) {
        banner.value = adsStore.adService.requestPageAd({
            type: props.type,
            refresh: props.refreshSec,
            el: id,
            sizes: filteredSizes,
        })

        banner.value.addEventListener('rendered', () => {
            isBannerVisible.value = true
            emit('update:visible', true)
        })
        banner.value.addEventListener('empty', () => {
            isBannerVisible.value = false
            emit('update:visible', false)
        })
    }
}

const updateBannerThrottled = useThrottleFn(updateBanner, 1000)

async function checkEmptyStyles() {
    if (!props.emptyClass) {
        return
    }

    await idleCallbackPromise()

    const elem = containerRef.value?.appendChild(document.createElement('div'))
    if (elem) {
        elem.classList.add(props.emptyClass)
        if (window.getComputedStyle(elem).display === 'none') {
            console.error(
                `AdBlock: class .${props.emptyClass} sets display to "none". That breaks tracking of the future size changes.\n\nUse negative margins to hide empty ad in the layout.`,
            )
        }
        elem.remove()
    }
}

onMounted(() => {
    checkEmptyStyles()
    updateBannerThrottled()
})

watch(
    () => props.emptyClass,
    () => {
        checkEmptyStyles()
    },
)

watch(
    () => adDisabledStore.isAdDisabled,
    (value) => {
        // destroy the previous banner
        banner.value?.destroy()
        banner.value = undefined
        if (!value) {
            updateBannerThrottled()
        }
    },
)

useResizeObserver(containerRef, updateBannerThrottled)

if (props.intersectionRootMargin !== undefined) {
    const { stop } = useIntersectionObserver(
        containerRef,
        ([entry]) => {
            if (entry?.isIntersecting) {
                readyForAdRequest.value = true
                updateBannerThrottled()
                stop()
            }
        },
        {
            rootMargin: props.intersectionRootMargin,
        },
    )
}

onBeforeUnmount(() => {
    banner.value?.destroy()
})
</script>

<style module>
.wrapper {
    width: fit-content;
    margin: 0 auto;
    position: relative;
    pointer-events: none;
    min-height: var(--placeholder-height);
    max-height: var(--slot-max-height);
}

/* fluid ads has inline style with "width: 100%". In that case wrapper should also take all space */
.wrapper:has(div[style*='width: 100%;']) {
    width: 100%;
}

.container {
    /* TODO: check ad requirements and remove if not needed */
    min-width: 1px;
    min-height: 1px;
    pointer-events: auto;
    overflow: hidden;
    max-height: var(--slot-max-height);
}
</style>
